/**
 * Read-only Leaflet map for a device's recent GPS fixes.
 *
 * Why this component exists:
 *   The DeviceDetailPage already lists the lat/lon rows in a table, but
 *   numbers alone don't tell an operator "this device has been moving
 *   across town for three days." A small embed with the recent fixes
 *   pinned on an OSM tile layer turns the same data into an at-a-glance
 *   recovery / behaviour signal.
 *
 * Design choices:
 *   - CircleMarker (vector, drawn by Leaflet) instead of default icon
 *     markers. The default markers need their PNG URLs rewritten for
 *     bundlers (a well-known Leaflet+Vite gotcha) and we don't need the
 *     pin shape — coloured dots are clearer at this scale.
 *   - The newest fix gets a larger filled marker; older fixes are smaller
 *     and translucent so the eye picks up "most recent" first.
 *   - A Polyline connects oldest→newest so a moving device shows a track.
 *   - Bounds auto-fit to all fixes on mount + whenever the rows change.
 *   - OpenStreetMap tiles: free, no API key. Attribution is required
 *     and rendered by Leaflet's default control.
 */
import { useEffect } from "react";
import { LatLngBounds, type LatLngBoundsLiteral } from "leaflet";
import { CircleMarker, MapContainer, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import type { DeviceLocationEvent } from "@dfp/api-client";
import "leaflet/dist/leaflet.css";

interface DeviceLocationMapProps {
  readonly rows: readonly DeviceLocationEvent[];
}

export function DeviceLocationMap({ rows }: DeviceLocationMapProps): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">
        No GPS fixes recorded yet.
      </div>
    );
  }
  // The API returns rows newest-first. The Polyline + the "newest" marker
  // logic both want oldest→newest, so reverse once at the top.
  const ordered = [...rows].reverse();
  const newest = ordered[ordered.length - 1]!;
  const polyline: [number, number][] = ordered.map((r) => [r.lat, r.lon]);

  return (
    <div className="h-64 overflow-hidden rounded-md border border-slate-200">
      <MapContainer
        center={[newest.lat, newest.lon]}
        zoom={13}
        scrollWheelZoom={false}
        className="h-full w-full"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {ordered.length > 1 ? (
          <Polyline
            positions={polyline}
            pathOptions={{ color: "#6366f1", weight: 2, opacity: 0.6 }}
          />
        ) : null}
        {ordered.map((row, idx) => {
          const isNewest = idx === ordered.length - 1;
          return (
            <CircleMarker
              key={row.id}
              center={[row.lat, row.lon]}
              radius={isNewest ? 8 : 5}
              pathOptions={{
                color: isNewest ? "#4338ca" : "#94a3b8",
                fillColor: isNewest ? "#6366f1" : "#cbd5e1",
                fillOpacity: isNewest ? 0.85 : 0.5,
                weight: 1,
              }}
            >
              <Popup>
                <div className="text-xs">
                  <a
                    href={`https://www.google.com/maps?q=${row.lat},${row.lon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-brand-600 hover:underline"
                    title="Open in Google Maps"
                  >
                    {row.lat.toFixed(5)}, {row.lon.toFixed(5)}
                  </a>
                  <div className="text-slate-500">{new Date(row.observedAt).toLocaleString()}</div>
                  <div className="text-slate-500">
                    {row.source}
                    {row.accuracyM === null ? "" : ` · ± ${row.accuracyM.toFixed(0)} m`}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
        <FitBoundsToRows rows={ordered} />
      </MapContainer>
    </div>
  );
}

/**
 * Effect-only child that asks the parent MapContainer to re-fit its
 * bounds whenever the underlying rows change. Lives inside the
 * MapContainer so `useMap()` resolves.
 */
function FitBoundsToRows({ rows }: { rows: readonly DeviceLocationEvent[] }): null {
  const map = useMap();
  useEffect(() => {
    if (rows.length === 0) return;
    if (rows.length === 1) {
      const r = rows[0]!;
      map.setView([r.lat, r.lon], 14, { animate: false });
      return;
    }
    const literal: LatLngBoundsLiteral = rows.map((r) => [r.lat, r.lon]);
    const bounds = new LatLngBounds(literal);
    // padding so markers near the edge are not clipped by the container.
    map.fitBounds(bounds, { padding: [24, 24], animate: false, maxZoom: 16 });
  }, [map, rows]);
  return null;
}
